import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './AdminCopyMenu.css';

/**
 * @typedef {{ label: string; onSelect: () => void | Promise<void>; disabled?: boolean; showCopiedFeedback?: boolean }} CopyMenuItem
 */

/**
 * @param {object} props
 * @param {CopyMenuItem[]} props.items
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel]
 */
export default function AdminCopyMenu({
  items,
  disabled = false,
  className = '',
  ariaLabel = 'Copy options',
}) {
  const list = Array.isArray(items) ? items : [];
  const triggerDisabled = disabled || list.length === 0;

  const [open, setOpen] = useState(false);
  const [copiedFlash, setCopiedFlash] = useState(false);
  const [menuFixedStyle, setMenuFixedStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const flashTimerRef = useRef(null);
  const menuIdRef = useRef(`admin-copy-menu-${Math.random().toString(36).slice(2, 10)}`);

  useEffect(() => {
    if (triggerDisabled) setOpen(false);
  }, [triggerDisabled]);

  const getTriggerElement = useCallback(() => {
    return (
      triggerRef.current ||
      rootRef.current?.querySelector(':scope > button.admin-copy-menu__trigger')
    );
  }, []);

  const updateMenuFixedPosition = useCallback(() => {
    const el = getTriggerElement();
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuFixedStyle({
      top: r.bottom + 4,
      right: Math.max(8, window.innerWidth - r.right),
      minWidth: Math.max(220, r.width),
    });
  }, [getTriggerElement]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuFixedStyle(null);
      return undefined;
    }
    updateMenuFixedPosition();
    const scrollRoot = getTriggerElement()?.closest('.content') || null;
    const onScrollOrResize = () => updateMenuFixedPosition();
    window.addEventListener('resize', onScrollOrResize);
    scrollRoot?.addEventListener('scroll', onScrollOrResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      scrollRoot?.removeEventListener('scroll', onScrollOrResize);
    };
  }, [open, updateMenuFixedPosition, getTriggerElement]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDoc);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  const clearFlashTimer = useCallback(() => {
    if (flashTimerRef.current != null) {
      window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearFlashTimer(), [clearFlashTimer]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (triggerDisabled) return;
    setOpen((prev) => !prev);
  };

  const handleItemClick = async (item) => {
    if (item.disabled) return;
    await item.onSelect();
    setOpen(false);
    const showFeedback = item.showCopiedFeedback !== false;
    if (!showFeedback) return;
    clearFlashTimer();
    setCopiedFlash(true);
    flashTimerRef.current = window.setTimeout(() => {
      setCopiedFlash(false);
      flashTimerRef.current = null;
    }, 1500);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') setOpen(false);
  };

  const placementReady = menuFixedStyle != null;
  const ariaExpanded = !triggerDisabled && open;

  return (
    <div
      ref={rootRef}
      className={`admin-copy-menu ${className}`.trim()}
      onKeyDown={onKeyDown}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`admin-copy-menu__trigger${copiedFlash ? ' admin-copy-menu__trigger--copied' : ''}`}
        disabled={triggerDisabled}
        aria-expanded={ariaExpanded ? 'true' : 'false'}
        aria-haspopup="true"
        aria-controls={triggerDisabled ? undefined : menuIdRef.current}
        aria-label={ariaLabel}
        onClick={handleToggle}
      >
        {copiedFlash ? 'Copied' : 'Copy'}
        <span className="admin-copy-menu__chevron" aria-hidden>
          ▼
        </span>
      </button>
      {open &&
        createPortal(
          <ul
            ref={menuRef}
            id={menuIdRef.current}
            className="admin-copy-menu__list admin-copy-menu__list--fixed"
            role="menu"
            style={{
              position: 'fixed',
              zIndex: 1300,
              top: placementReady ? menuFixedStyle.top : -9999,
              right: placementReady ? menuFixedStyle.right : 8,
              minWidth: placementReady ? menuFixedStyle.minWidth : 220,
              visibility: placementReady ? 'visible' : 'hidden',
            }}
            onKeyDown={onKeyDown}
          >
            {list.map((item) => (
              <li key={item.label} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="admin-copy-menu__item"
                  disabled={!!item.disabled}
                  onClick={() => handleItemClick(item)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
}
